import type { RouteRecordNormalized } from 'vue-router';

import { USER_MANAGEMENT_GROUP } from '@/constants/sidebar-placeholder-groups';

import type { NavItem } from '@/types/navigation/sidebar-navigation';
import type { PageRouteMeta } from '@/types/page-route-meta';

type OrderedLabel = Pick<NavItem, 'label' | 'order'>;
type NavSubItem = NonNullable<NavItem['subRoutes']>[number];

type MenuRouteEntry = {
    route: RouteRecordNormalized;
    meta: PageRouteMeta;
};

function byOrderThenLabel(a: OrderedLabel, b: OrderedLabel): number {
    const ao = a.order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order ?? Number.MAX_SAFE_INTEGER;

    return ao === bo ? a.label.localeCompare(b.label) : ao - bo;
}

const getMeta = (route: RouteRecordNormalized): PageRouteMeta => (route.meta ?? {}) as PageRouteMeta;

const getLabel = (route: RouteRecordNormalized): string =>
    getMeta(route).title ?? (typeof route.name === 'string' ? route.name : route.path);

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const hasParentRouteName = (name: unknown, parentKeys: Set<string>): boolean =>
    typeof name === 'string' && parentKeys.has(name);

function canAccessRoute(
    route: RouteRecordNormalized,
    userSessionStore: ReturnType<typeof useUserSessionStore>
): boolean {
    const auth = getMeta(route).auth;

    return !auth || typeof auth !== 'object' || !('roles' in auth) || userSessionStore.isInRole(auth.roles);
}

function createNavItem(label: string, icon: string, order?: number, route?: string, subRoutes?: NavSubItem[]): NavItem {
    const hasRoute = isNonEmptyString(route);
    const hasSubRoutes = Array.isArray(subRoutes) && subRoutes.length > 0;

    return {
        label,
        icon,
        order,
        route,
        subRoutes,
        hasRoute,
        hasSubRoutes
    };
}

export function useSidebarNavigation() {
    const router = useRouter();
    const route = useRoute();
    const userSessionStore = useUserSessionStore();

    const sidebarNavigationItems = computed<NavItem[]>(() => {
        const menuRoutes: MenuRouteEntry[] = router
            .getRoutes()
            .map((route) => ({
                route,
                meta: getMeta(route)
            }))
            .filter(({ route, meta }) => meta.showInMenu === true && canAccessRoute(route, userSessionStore));

        const groupChildren = new Map<string, NavSubItem[]>();
        const parentKeys = new Set<string>();

        for (const entry of menuRoutes) {
            const { route, meta } = entry;
            const menuGroup = meta.menuGroup;

            if (!isNonEmptyString(menuGroup)) {
                continue;
            }

            parentKeys.add(menuGroup);

            const list = groupChildren.get(menuGroup) ?? [];

            list.push({
                label: getLabel(route),
                icon: meta.menuIcon ?? '',
                order: meta.menuOrder,
                route: route.path
            });

            groupChildren.set(menuGroup, list);
        }

        const parents = new Map<string, RouteRecordNormalized>();

        for (const entry of menuRoutes) {
            const name = entry.route.name;
            if (typeof name !== 'string' || !parentKeys.has(name)) {
                continue;
            }

            parents.set(name, entry.route);
        }

        const singles: NavItem[] = [];

        for (const entry of menuRoutes) {
            const { route, meta } = entry;

            if (isNonEmptyString(meta.menuGroup)) {
                continue;
            }

            if (hasParentRouteName(route.name, parentKeys)) {
                continue;
            }

            singles.push(createNavItem(getLabel(route), meta.menuIcon ?? '', meta.menuOrder, route.path));
        }

        const groups: NavItem[] = [];

        for (const [groupKey, children] of groupChildren.entries()) {
            const parentRoute = parents.get(groupKey);
            const placeholder =
                groupKey === USER_MANAGEMENT_GROUP
                    ? { label: 'User Management', icon: 'br-icon:user-settings', order: 1 }
                    : undefined;

            const label = parentRoute ? getLabel(parentRoute) : (placeholder?.label ?? groupKey);
            const icon = parentRoute ? (getMeta(parentRoute).menuIcon ?? '') : (placeholder?.icon ?? '');
            const order = parentRoute ? getMeta(parentRoute).menuOrder : placeholder?.order;

            const routePath = parentRoute?.path;

            groups.push(createNavItem(label, icon, order, routePath, [...children].sort(byOrderThenLabel)));
        }

        return [...singles, ...groups].sort(byOrderThenLabel);
    });

    const matchedGroupLabels = computed<Set<string>>(() => {
        const currentPath = route.path;
        const result = new Set<string>();

        for (const item of sidebarNavigationItems.value) {
            if (!item.subRoutes?.length) {
                continue;
            }

            const isMatched = item.subRoutes.some(
                (sub) => currentPath === sub.route || currentPath.startsWith(sub.route + '/')
            );

            if (isMatched) {
                result.add(item.label);
            }
        }

        return result;
    });

    return {
        sidebarNavigationItems,
        matchedGroupLabels
    };
}
